import { QueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import { unescape } from 'underscore';

import { ActivityResponse } from '~teacher/hook';
import { queryKey } from '~teacher/hook/activity/queryKey';

import { findStudentIdByUuid } from '../../utils';
import { StudentResponsePubnubData } from '../types';

// snake case keys from messages are mapped to camelCase local versions.

export const updateStudentResponse = produce(
  (
    activityData: ActivityResponse,
    {
      user_uuid,
      hidden,
      id,
      is_correct,
      question,
      selection_answers,
      team,
      text_answers,
    }: StudentResponsePubnubData
  ) => {
    const formattedResponse = mapStudentResponse({
      hidden,
      id,
      textAnswers: text_answers.map((textAnswer) => ({
        answerText: unescape(textAnswer.answer_text),
      })),
      isCorrect: is_correct,
      selectionAnswers: selection_answers.map((selectionAnswer) => ({
        answerId: selectionAnswer.answer_id,
      })),
      userUuid: user_uuid,
      question,
      team,
    });

    const studentId = findStudentIdByUuid(
      activityData,
      formattedResponse.userUuid
    );

    if (studentId) {
      activityData.students[studentId].isPresent = true;
    }

    activityData.responses[formattedResponse.id] = formattedResponse;
  }
);

const mapStudentResponse = ({
  hidden,
  id,
  textAnswers,
  isCorrect,
  question,
  selectionAnswers,
  team,
  userUuid,
}: {
  hidden: boolean;
  id: number;
  textAnswers: Array<{ answerText: string }>;
  isCorrect: boolean;
  question: number;
  selectionAnswers: Array<{ answerId: number }>;
  team: number;
  userUuid: string;
}) => ({
  hidden: hidden,
  id: id,
  textResponse: textAnswers.map((textAnswer) => textAnswer.answerText)[0],
  isCorrect: isCorrect,
  questionId: question,
  selectedAnswerIds: selectionAnswers.map(
    (selectionAnswer) => selectionAnswer.answerId
  ),
  team,
  userUuid: userUuid,
});

export const studentResponseCacheUpdater = ({
  queryClient,
  activityInstanceId,
  rawResponse,
}: {
  queryClient: QueryClient;
  activityInstanceId: number;
  rawResponse: StudentResponsePubnubData;
}) => {
  queryClient.setQueryData(
    queryKey.byId(activityInstanceId),
    (activityData: ActivityResponse) =>
      updateStudentResponse(activityData, rawResponse)
  );
};
